/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Tag, Link, ClickableTile } from '@carbon/react';
import { ArrowRight } from '@carbon/react/icons';
import type { TagTypeName } from '@carbon/type';
import React from 'react';
import { useIntl } from 'react-intl';

import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { links } from '../links';
import { ResourcesTab as messages } from '../messages';
import { useResourcesData } from './resourcesData';
import './Resources.scss';

export const Resources = (_props: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const { resourcesData } = useResourcesData();

  const demoSiteLink = (
    <Link
      className="Resources-link"
      href={links.ResourcesDemoSiteTileLink}
      target="_blank"
      rel="noopener noreferrer">
      {t(messages.demoSiteTag)}
    </Link>
  );

  const docsTutorialLink = (
    <Link
      className="Resources-link"
      href={links.ResourcesDocsTutorialTileLink}
      target="_blank"
      rel="noopener noreferrer">
      {t(messages.docsTutorialTitle)}
    </Link>
  );

  return (
    <TabContent title={'Explore your resources'}>
      <div className="Resources">
        <div className="Resources-tiles">
          <ClickableTile
            className="Resources-demo-site__tile"
            href={links.ResourcesDemoSiteTileLink}
            rel="noopener noreferrer"
            tabIndex={0}>
            <Tag className="Resources-tile__tag" type="teal">
              {t(messages.demoSiteTag)}
            </Tag>
            <div className="Resources-tile__title">
              <span>{t(messages.demoSiteTileTitle)}</span>
              <ArrowRight size={16} className="Resources-tile__arrow" />
            </div>
          </ClickableTile>
          <ClickableTile
            className="Resources-docs-tutorial__tile"
            href={links.ResourcesDocsTutorialTileLink}
            rel="noopener noreferrer"
            tabIndex={0}>
            <div className="Resources-tile__tags">
              <Tag className="Resources-tile__tag" type="purple">
                {t(messages.docsTag)}
              </Tag>
              <Tag className="Resources-tile__tag" type="green">
                {t(messages.tutorialTag)}
              </Tag>
            </div>
            <div className="Resources-tile__title">
              <span>{t(messages.docsTutorialTileTitle)}</span>
              <ArrowRight size={16} className="Resources-tile__arrow" />
            </div>
          </ClickableTile>
        </div>
        {resourcesData.map(section => {
          return (
            <div key={section['section-title']}>
              <div className="Resources-Section__title">
                <h4>{section['section-title']}</h4>
                <p>{section['section-subTitle']}</p>
              </div>
              <div className="Resources-Section__tiles">
                {section.tiles.map(tile => {
                  return (
                    <div key={tile['tile-title']}>
                      <ClickableTile
                        className="Resources-Section__tile"
                        href={tile['tile-link']}
                        rel="noopener noreferrer"
                        tabIndex={0}>
                        <Tag
                          className="Resources-Section__tile-tag"
                          type={tile['tag-type'] as TagTypeName}>
                          {[tile['tag-title']]}
                        </Tag>
                        <div className="Resources-Section__tile-title">
                          {[tile['tile-title']]}
                        </div>
                        {tile['tile-text'] && (
                          <div className="Resources-Section__tile-preview-text">
                            {[tile['tile-text']]}
                          </div>
                        )}
                        {tile['tile-image'] && (
                          <img
                            src={tile['tile-image']}
                            className="Resources-Section__tile-preview-image"
                            alt=""
                          />
                        )}
                      </ClickableTile>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="Resources-Footer">
          {t(messages.footer, {
            demoSiteLink,
            docsTutorialLink
          })}
        </div>
      </div>
    </TabContent>
  );
};
